import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ArrowLeft } from "lucide-react";

const EMPTY = { n_ref: "", modelo: "", orden: 0, peso_unitario_kg: 0 };

export default function ConfiguracionProductos() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(EMPTY);

  const load = async () => { const { data } = await api.get("/products"); setRows(data || []); };
  useEffect(() => { load(); }, []);

  const guardar = async () => {
    if (!cur.n_ref || !cur.modelo) return toast.error("Ref y modelo son obligatorios");
    const payload = { n_ref: cur.n_ref, modelo: cur.modelo, orden: Number(cur.orden) || 0, peso_unitario_kg: Number(cur.peso_unitario_kg) || 0 };
    try {
      if (cur.id) await api.put(`/products/${cur.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Producto guardado"); setOpen(false); load();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try { await api.delete(`/products/${id}`); setRows((p) => p.filter((x) => x.id !== id)); toast.success("Eliminado"); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="productos-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/configuracion")}><ArrowLeft className="h-4 w-4 mr-2" /> Configuración</Button>
      <div className="flex justify-between items-center">
        <div><h1 className="text-3xl font-bold tracking-tight">Productos</h1><p className="text-muted-foreground mt-2">Catálogo de modelos y pesos.</p></div>
        <Button onClick={() => { setCur(EMPTY); setOpen(true); }} data-testid="nuevo-producto-btn"><Plus className="h-4 w-4 mr-2" /> Nuevo</Button>
      </div>
      <Card><CardContent className="pt-6">
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Ref</TableHead><TableHead>Modelo</TableHead><TableHead className="text-right">Orden</TableHead><TableHead className="text-right">Peso (kg)</TableHead><TableHead className="text-right w-24"></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono text-xs">{p.n_ref}</TableCell>
                  <TableCell className="font-medium">{p.modelo}</TableCell>
                  <TableCell className="text-right">{p.orden}</TableCell>
                  <TableCell className="text-right">{Number(p.peso_unitario_kg).toFixed(2)}</TableCell>
                  <TableCell className="text-right"><div className="flex justify-end gap-2">
                    <Button variant="outline" size="icon" onClick={() => { setCur({ id: p.id, n_ref: p.n_ref, modelo: p.modelo, orden: p.orden, peso_unitario_kg: p.peso_unitario_kg }); setOpen(true); }} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="destructive" size="icon" onClick={() => eliminar(p.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{cur.id ? "Editar" : "Nuevo"} producto</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Referencia</Label><Input value={cur.n_ref} onChange={(e) => setCur({ ...cur, n_ref: e.target.value })} data-testid="prod-nref" /></div>
            <div className="space-y-2"><Label>Modelo</Label><Input value={cur.modelo} onChange={(e) => setCur({ ...cur, modelo: e.target.value })} data-testid="prod-modelo" /></div>
            <div className="space-y-2"><Label>Orden</Label><Input type="number" value={cur.orden} onChange={(e) => setCur({ ...cur, orden: e.target.value })} /></div>
            <div className="space-y-2"><Label>Peso unitario (kg)</Label><Input type="number" step="0.01" value={cur.peso_unitario_kg} onChange={(e) => setCur({ ...cur, peso_unitario_kg: e.target.value })} data-testid="prod-peso" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={guardar} data-testid="prod-save">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
